import { compact } from 'lodash';

/**
 * Primary drug text shared by the bulk queue, the prescription review queue and the central fill
 * queue: "<drug> <strength>", e.g. "ABC 15mg". Strength is optional — an item without one renders
 * as just the drug name.
 *
 * Pack size and form are deliberately NOT part of this string: they render on their own line
 * underneath, in smaller text. See formatPackSize / the PackSizeText component.
 *
 * Kept as its own module rather than in common/utils so the queue tables can import it without
 * pulling in that barrel's heavier dependencies.
 */
const formatDrugLabel = ({ drugName, strength } = {}) => compact([drugName, strength]).join(' ');

/**
 * Secondary line under the drug name, combining pack size and dosage form:
 *
 *   value + unit + form  ->  "pack size: 1ML (vial)"
 *   form only            ->  "(vial)"
 *   pack size only       ->  "pack size: 1ML"
 *   neither              ->  null
 *
 * The "pack size:" label only appears when BOTH halves of the pack size are present — half a
 * pack size ("1", or "ML") tells the technician nothing, so an incomplete one is dropped and the
 * form still shows on its own. Returns null when there is nothing to say, which callers use to
 * omit the line entirely.
 *
 * Both values render exactly as the catalog stores them — formAlias is catalog.form_alias, read
 * by the resolved product_id, so it is absent whenever no product resolved.
 */
export const formatPackSize = ({ packSizeValue, packSizeUnit, formAlias } = {}) => {
  const packSize =
    packSizeValue && packSizeUnit ? `pack size: ${packSizeValue}${packSizeUnit}` : null;

  return compact([packSize, formAlias ? `(${formAlias})` : null]).join(' ') || null;
};

export default formatDrugLabel;
